import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Configuration for each filter field
 */
export interface FilterFieldConfig<T = any> {
  /** URL parameter key */
  key: string;
  /** Default value */
  defaultValue: T;
  /** Parse function to convert URL string to typed value */
  parse?: (value: string | null) => T;
  /** Serialize function to convert typed value to URL string */
  serialize?: (value: T) => string | null;
}

/**
 * Infer the filter state type from config
 */
type InferFiltersFromConfig<T extends Record<string, FilterFieldConfig>> = {
  [K in keyof T]: T[K] extends FilterFieldConfig<infer U> ? U : never;
};

/**
 * Custom hook for managing filters with URL persistence
 *
 * @example
 * ```tsx
 * const filterConfig = {
 *   studentName: {
 *     key: 'name',
 *     defaultValue: '',
 *     parse: (v) => v || '',
 *     serialize: (v) => v || null,
 *   },
 *   classId: {
 *     key: 'class',
 *     defaultValue: null as number | null,
 *     parse: (v) => v ? Number(v) : null,
 *     serialize: (v) => v ? String(v) : null,
 *   },
 * } as const;
 *
 * const { filters, setFilter, setFilters, clearFilters, applyFilters } =
 *   useUrlFilters(filterConfig);
 *```
 */
export function useUrlFilters<T extends Record<string, FilterFieldConfig>>(
  config: T,
): {
  filters: InferFiltersFromConfig<T>;
  setFilter: <K extends keyof InferFiltersFromConfig<T>>(
    key: K,
    value: InferFiltersFromConfig<T>[K],
  ) => void;
  setFilters: (newFilters: Partial<InferFiltersFromConfig<T>>) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
  getActiveFilters: () => InferFiltersFromConfig<T>;
};

export function useUrlFilters<
  NS extends string,
  T extends Record<string, FilterFieldConfig>,
>(
  namespace: NS,
  config: T,
): {
  filters: InferFiltersFromConfig<T>;
  setFilter: <K extends keyof InferFiltersFromConfig<T>>(
    key: K,
    value: InferFiltersFromConfig<T>[K],
  ) => void;
  setFilters: (newFilters: Partial<InferFiltersFromConfig<T>>) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
  getActiveFilters: () => InferFiltersFromConfig<T>;
};

export function useUrlFilters<
  NS extends string,
  T extends Record<string, FilterFieldConfig>,
>(namespaceOrConfig: NS | T, configArg?: T) {
  const namespace =
    configArg === undefined ? undefined : (namespaceOrConfig as string);
  const config = (configArg === undefined ? namespaceOrConfig : configArg) as T;
  type Filters = InferFiltersFromConfig<T>;

  const [searchParams, setSearchParams] = useSearchParams();
  const namespacePrefix = useMemo(
    () => (namespace ? `${namespace}_` : ""),
    [namespace],
  );

  const getNamespacedKey = useCallback(
    (key: string) => `${namespacePrefix}${key}`,
    [namespacePrefix],
  );

  const parseFiltersFromUrl = useCallback((): Filters => {
    const filters = {} as Filters;

    for (const [fieldName, fieldConfig] of Object.entries(config)) {
      const urlValue = searchParams.get(getNamespacedKey(fieldConfig.key));

      if (fieldConfig.parse) {
        filters[fieldName as keyof Filters] = fieldConfig.parse(
          urlValue,
        ) as any;
      } else {
        filters[fieldName as keyof Filters] = (urlValue ??
          fieldConfig.defaultValue) as any;
      }
    }

    return filters;
  }, [searchParams, config, getNamespacedKey]);

  const [filters, setFiltersState] = useState<Filters>(parseFiltersFromUrl);

  const syncFiltersToUrl = useCallback(
    (newFilters: Filters, replace: boolean = true) => {
      const newParams = new URLSearchParams(searchParams);

      for (const [fieldName, fieldConfig] of Object.entries(config)) {
        const value = newFilters[fieldName as keyof Filters];

        let urlValue: string | null;

        if (fieldConfig.serialize) {
          urlValue = fieldConfig.serialize(value);
        } else {
          if (value === null || value === undefined || value === "") {
            urlValue = null;
          } else {
            urlValue = String(value);
          }
        }

        const paramKey = getNamespacedKey(fieldConfig.key);

        if (urlValue) {
          newParams.set(paramKey, urlValue);
        } else {
          newParams.delete(paramKey);
        }
      }

      setSearchParams(newParams, { replace });
    },
    [config, searchParams, setSearchParams, getNamespacedKey],
  );

  useEffect(() => {
    const urlFilters = parseFiltersFromUrl();
    setFiltersState(urlFilters);
  }, [searchParams, parseFiltersFromUrl]);

  const setFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) => {
      setFiltersState((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const setFilters = useCallback((newFilters: Partial<Filters>) => {
    setFiltersState((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  const applyFilters = useCallback(() => {
    syncFiltersToUrl(filters, false);
  }, [filters, syncFiltersToUrl]);

  const clearFilters = useCallback(() => {
    const defaultFilters = {} as Filters;

    for (const [fieldName, fieldConfig] of Object.entries(config)) {
      defaultFilters[fieldName as keyof Filters] =
        fieldConfig.defaultValue as any;
    }

    setFiltersState(defaultFilters);
    syncFiltersToUrl(defaultFilters, false);
  }, [config, syncFiltersToUrl]);

  const hasActiveFilters = useCallback((): boolean => {
    for (const [fieldName, fieldConfig] of Object.entries(config)) {
      const value = filters[fieldName as keyof Filters];
      const defaultValue = fieldConfig.defaultValue;

      if (
        value !== defaultValue &&
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {
        return true;
      }
    }
    return false;
  }, [filters, config]);

  const getActiveFilters = useCallback((): Filters => {
    return parseFiltersFromUrl();
  }, [parseFiltersFromUrl]);

  return {
    filters,
    setFilter,
    setFilters,
    applyFilters,
    clearFilters,
    hasActiveFilters,
    getActiveFilters,
  };
}
